import { Modal, ModalContent, ModalHeader, ModalBody } from "@nextui-org/react";
import { Button } from "@nextui-org/react";
import { Tabs, Tab } from "@nextui-org/react";
import { PostType } from "@/types/post";
import { format } from "date-fns";
import { Pencil, Trash2, X } from "lucide-react";

interface DraftsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drafts: PostType[];
  scheduledPosts: PostType[];
  onEdit: (post: PostType) => void;
  onDelete: (post: PostType) => void;
  onCancelScheduled: (post: PostType) => void;
}

export function DraftsModal({
  open,
  onOpenChange,
  drafts,
  scheduledPosts,
  onEdit,
  onDelete,
  onCancelScheduled,
}: DraftsModalProps) {
  const PostList = ({ posts, isScheduled = false }: { posts: PostType[], isScheduled?: boolean }) => {
    if (posts.length === 0) {
      return (
        <div className="text-center text-secondary py-8">
          {isScheduled ? "No scheduled posts" : "No drafts"}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {posts?.map((post) => (
          <div
            key={post.id}
            className="border border-secondary rounded-lg p-4 space-y-2"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="line-clamp-2">{post.content}</p>
                {isScheduled && post.scheduledFor && (
                  <p className="text-sm text-secondary mt-1">
                    Scheduled for {format(new Date(post.scheduledFor), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  isIconOnly
                  variant="light"
                  onPress={() => onEdit(post)}
                  className="text-secondary"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  isIconOnly
                  variant="light"
                  onPress={() => isScheduled ? onCancelScheduled(post) : onDelete(post)}
                  className="text-secondary"
                >
                  {isScheduled ? <X className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal 
      isOpen={open} 
      onOpenChange={onOpenChange}
      scrollBehavior="inside"
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="text-xl">
              Drafts & Scheduled Posts
            </ModalHeader>
            <ModalBody className="pb-6">
              <div className="bg-card p-4 rounded-lg">
                <Tabs 
                  aria-label="Post categories"
                  variant="bordered"
                  fullWidth
                  classNames={{
                    tabList: "w-full grid grid-cols-2 gap-2",
                    tab: "h-10",
                    cursor: "bg-secondary",
                    tabContent: "group-data-[selected=true]:text-primary"
                  }}
                >
                  <Tab key="drafts" title="Drafts">
                    <div className="mt-4">
                      <PostList posts={drafts} />
                    </div>
                  </Tab>
                  <Tab key="scheduled" title="Scheduled">
                    <div className="mt-4">
                      <PostList posts={scheduledPosts} isScheduled />
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}